///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Scene object for one weather alert: a severity-colored area polygon
/// draped on the terrain. Alert areas are county/zone scale so the polygon
/// is the whole visual — there is no point marker. Clicking anywhere in the
/// area selects the alert, frames it, and opens its info panel. Only alerts
/// that carry geometry create features; the few zone-code-only alerts left
/// (EMMA-code MeteoAlarm countries) are data-only.
///

import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import type { Camera } from "@stovokor/camera/Camera"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { vec3 } from "@stovokor/math"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type DatasetSceneObject } from "../DatasetLayer"
import { AlertAreaNode, type AlertGeometry } from "./AlertAreaNode"
import { type AlertProperties } from "./AlertsTable"

// Clearance above the highest sampled terrain so the flat area plane clears
// ridges inside the polygon that the ring samples miss
const cAreaClearanceKm = 0.6
// Max terrain samples along an outer ring when finding its high point
const cMaxRingElevationSamples = 24

const cAlertFocusMinDistance = 30
const cAlertFocusMaxZoomRadius = 400
const cKmPerDegree = 111.2

// Alerts appear (dimmed) this long before their onset, so upcoming watches
// and warnings are visible while there is still time to act on them
const cUpcomingLeadMs = 24 * 60 * 60 * 1000

export class AlertFeature extends GestureNode implements DatasetSceneObject<AlertProperties>, InfoPanelProvider {
	public readonly focusable: boolean = true
	public readonly id: string

	public feature: AlertProperties
	public coordinate: geographic

	private readonly cameraAdapter: EntityCameraAdapter
	private readonly featureContext: EarthBrowserContext

	private area?: AlertAreaNode
	private hitSphere: sphere
	/** Half the alert bbox diagonal in km, used for hit test and framing */
	private extentRadiusKm: number
	/** False until terrain elevation was applied with a loaded elevation layer */
	private elevationResolved = false
	private focused = false

	/** Validity window in epoch ms, from onset/effective/sent to ends/expires */
	private startMs = -Infinity
	private endMs = Infinity
	/** Whether the sim clock is inside the display window (lead + validity) */
	private timeVisible = true
	private upcoming = false

	constructor(context: EarthBrowserContext, feature: AlertProperties) {
		super(context)
		this.id = feature.id
		this.feature = feature
		this.featureContext = context

		this.coordinate = new geographic(
			(feature.centroid_lon ?? 0) * trig.rad,
			(feature.centroid_lat ?? 0) * trig.rad,
			0
		)
		const xRot = matrix.axisRotation(vec3.unitX, this.coordinate.latitude + trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, this.coordinate.longitude + trig.pi)
		this.transform = matrix.translate(this.coordinate.cartesian).premultiply(yRot.premultiply(xRot))

		this.extentRadiusKm = this.computeExtentRadiusKm(feature)
		this.computeTimeWindow(feature)
		this.area = this.buildArea()

		// Clicking anywhere on or near the alert area selects the alert
		this.hitSphere = new sphere(this.extentRadiusKm)

		// Frame the whole area when focused: distance scales with extent
		const focusDistance = Math.max(cAlertFocusMinDistance, this.extentRadiusKm * 2.8)
		const maxZoomRadius = Math.max(cAlertFocusMaxZoomRadius, this.extentRadiusKm * 8)
		this.cameraAdapter = new EntityCameraAdapter(context, this, focusDistance, maxZoomRadius)
	}

	private computeExtentRadiusKm(feature: AlertProperties): number {
		if (feature.bbox_min_lat === null || feature.bbox_max_lat === null
			|| feature.bbox_min_lon === null || feature.bbox_max_lon === null) return 1
		const midLat = (feature.bbox_min_lat + feature.bbox_max_lat) / 2
		// Ingest stores antimeridian-crossing bboxes unwrapped (max may exceed
		// 180), so max - min is the true span. Guard against legacy rows where
		// a dateline-crossing zone produced a naive span of ~360°: no alert
		// area is wider than a hemisphere, so take the wrapped complement.
		let dLonDeg = feature.bbox_max_lon - feature.bbox_min_lon
		if (dLonDeg > 180) {
			dLonDeg = 360 - dLonDeg
		}
		const dLatKm = (feature.bbox_max_lat - feature.bbox_min_lat) * cKmPerDegree
		const dLonKm = dLonDeg * cKmPerDegree * Math.cos(midLat * trig.rad)
		return Math.max(Math.sqrt(dLatKm * dLatKm + dLonKm * dLonKm) / 2, 1)
	}

	private computeTimeWindow(feature: AlertProperties): void {
		const start = Date.parse(feature.onset ?? feature.effective ?? feature.sent)
		const end = Date.parse(feature.ends ?? feature.expires ?? "")
		this.startMs = Number.isNaN(start) ? -Infinity : start
		this.endMs = Number.isNaN(end) ? Infinity : end
	}

	private parseGeometry(): AlertGeometry | undefined {
		if (!this.feature.geometry) return undefined
		try {
			let geometry = JSON.parse(this.feature.geometry) as AlertGeometry | string
			if (typeof geometry === "string") {
				// Tolerate double-encoded geometry (jsonb stored as a JSON string)
				geometry = JSON.parse(geometry) as AlertGeometry
			}
			if (geometry?.type !== "Polygon" && geometry?.type !== "MultiPolygon") return undefined
			return geometry
		} catch {
			return undefined
		}
	}

	/**
	 * Highest terrain (km) sampled along each outer ring, so the flat alert
	 * plane drapes above every ridge it crosses
	 */
	private maxTerrainElevationKm(geometry: AlertGeometry): number {
		const elevation = this.featureContext.baseElevation
		let maxMeters = 0

		const polygons: number[][][][] =
			geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates
		for (const rings of polygons) {
			const outer = rings[0]
			if (!outer) continue
			const step = Math.max(1, Math.floor(outer.length / cMaxRingElevationSamples))
			for (let i = 0; i < outer.length; i += step) {
				const [lon, lat] = outer[i]
				const meters = elevation.getElevation(new geographic(lon * trig.rad, lat * trig.rad, 0))
				if (meters > maxMeters) {
					maxMeters = meters
				}
			}
		}
		return maxMeters / 1000
	}

	/**
	 * The area node is positioned in world space (its vertices are absolute
	 * ECEF), so it must not inherit this feature's centroid transform;
	 * parent it with an inverse transform like the wildfire perimeters.
	 */
	private buildArea(): AlertAreaNode | undefined {
		const geometry = this.parseGeometry()
		if (!geometry) return undefined

		const altitudeKm = this.maxTerrainElevationKm(geometry) + cAreaClearanceKm
		const area = new AlertAreaNode(this.featureContext, geometry, this.feature.severity, altitudeKm)
		area.transform = this.transform.invert()
		this.addNode(area)
		this.elevationResolved = this.featureContext.baseElevation.loaded
		return area
	}

	private rebuildArea(): void {
		if (this.area) {
			this.removeNode(this.area)
			this.area.destroy()
		}
		this.area = this.buildArea()
		this.area?.setFocused(this.focused)
		if (this.upcoming) {
			this.area?.setUpcoming(true)
		}
	}

	/**
	 * Areas built before the async elevation layer finished loading sit at
	 * sea level (possibly buried under terrain); re-drape them as soon as
	 * the elevation data is available.
	 *
	 * Visibility follows the simulation clock: hidden once the alert ends
	 * and until cUpcomingLeadMs before it starts, dimmed while upcoming.
	 * Scrubbing the timeline shows alerts coming into and out of effect.
	 * A focused alert stays visible so the info panel never points at
	 * nothing.
	 */
	override render(state: RenderState): RenderActionSet[] {
		const clockMs = this.featureContext.clock.clockTime.date.getTime()
		this.timeVisible = this.focused
			|| (clockMs >= this.startMs - cUpcomingLeadMs && clockMs <= this.endMs)
		if (!this.timeVisible) {
			return []
		}

		const upcoming = clockMs < this.startMs
		if (upcoming !== this.upcoming) {
			this.upcoming = upcoming
			this.area?.setUpcoming(upcoming)
		}

		if (!this.elevationResolved && this.featureContext.baseElevation.loaded) {
			this.rebuildArea()
		}
		return super.render(state)
	}

	/** Called on every dataset poll; rebuilds the area when the alert changed */
	public updateFeature(feature: AlertProperties): void {
		const changed = feature.sent !== this.feature.sent
			|| feature.severity !== this.feature.severity
			|| (feature.geometry?.length ?? 0) !== (this.feature.geometry?.length ?? 0)
		this.feature = feature
		this.computeTimeWindow(feature)
		if (changed) {
			this.extentRadiusKm = this.computeExtentRadiusKm(feature)
			this.hitSphere = new sphere(this.extentRadiusKm)
			this.rebuildArea()
		}
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		return {
			title: this.feature.event,
			subtitle: this.feature.area_desc ?? undefined,
			theme: "alert",
			contentType: "alert",
			contentData: {
				type: "alert",
				alert: this.feature
			}
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.focused = true
		this.cameraAdapter.attach(camera)
		this.area?.setFocused(true)

		if (this.feature.id) {
			pushTargetToHistory('alert', String(this.feature.id))
		}
	}

	public onFocusLost(): void {
		this.focused = false
		this.cameraAdapter.detach()
		this.area?.setFocused(false)
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		// Alerts outside their display window are invisible, so unclickable
		if (!this.timeVisible) return undefined
		const dist = this.hitSphere.intersect(state.ray)
		if (dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}
}
